/**
 * Pure auth-state derivation — spec 028-h1-route-404, plan D-1.
 *
 * `useAuth()`'s underlying query answers with `status` / `fetchStatus` /
 * `data`, and those three fields alone are not enough to safely branch a
 * route on: `isLoading` collapses several genuinely different situations
 * into one boolean, which is exactly the gap that let a signed-out visitor
 * at a real route see "We can't find that page" (QA H1, Half B).
 *
 * `fetchStatus` is read BEFORE `status` for the paused case. React Query 5's
 * default `networkMode: 'online'` PAUSES a query while the browser reports
 * itself offline: no HTTP request is ever issued, `status` stays whatever it
 * already was (`'pending'` on a cold load), and `isLoading` is `false` — so a
 * consumer that branches on `isLoading` alone reads "not loading, no data" as
 * "the session was checked and refused," when the truth is "the session was
 * never checked at all." That gap is the C3 post-mortem's exact failure
 * shape, reproduced here for `/api/auth/user` (evidence §7 scenario C).
 */

export type AuthResolution = 'pending' | 'authenticated' | 'refused' | 'unreachable';

export interface AuthResolutionInput {
  status: 'pending' | 'error' | 'success';
  fetchStatus: 'fetching' | 'paused' | 'idle';
  data: unknown;
}

/**
 * Precedence (plan D-1, early-return — do not reorder without re-reading the
 * comment above each branch):
 *
 * 1. `status === 'error'` -> `'unreachable'`, regardless of `fetchStatus`.
 *    The query definitively failed (5xx, thrown network error, etc.).
 * 2. `status === 'pending' && fetchStatus === 'paused'` -> `'unreachable'`.
 *    No request was ever sent (offline pause) — never confuse this with
 *    `'refused'`, which requires the server to have actually answered.
 * 3. `status === 'pending'` (any other `fetchStatus`) -> `'pending'`. The
 *    request is genuinely in flight, or about to be.
 * 4. `status === 'success'` — the server answered. `data == null` means
 *    `getQueryFn({ on401: 'returnNull' })` mapped a 401 to `null`
 *    (`authQueryContract.test.ts` pins this as the ONLY way a successful
 *    query resolves to `null`), so that is `'refused'`. Any other `data` is
 *    `'authenticated'` — including a `'paused'` `fetchStatus` on a query that
 *    already succeeded once (e.g. going offline after a successful fetch):
 *    cached authenticated state must not be discarded just because the
 *    browser has since gone offline.
 */
export function deriveAuthResolution(input: AuthResolutionInput): AuthResolution {
  const { status, fetchStatus, data } = input;

  if (status === 'error') {
    return 'unreachable';
  }
  if (status === 'pending' && fetchStatus === 'paused') {
    return 'unreachable';
  }
  if (status === 'pending') {
    return 'pending';
  }
  // status === 'success'
  return data === null || data === undefined ? 'refused' : 'authenticated';
}
